import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LuChevronRight, LuPanelLeftClose, LuPanelLeftOpen, LuSearch } from './icons';
import { NavIcon } from './navIcons';
import { isDesignSystemOn } from '../../theme/mmecDsPreview';
import { navLabel } from './navLabelsAr';

export interface NavItem {
  id: string;
  title: string;
  icon: string;
}

export interface NavCategory {
  id: string;
  title: string;
  icon: string;
  items: NavItem[];
}

interface MMECNavRailProps {
  categories: NavCategory[];
  activeId: string;
  onSelect: (id: string) => void;
  /** Icon-only mode. The rail is 56px wide and labels are hidden. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Opens the command palette. Rendered as a search affordance at the top. */
  onOpenSearch?: () => void;
  language?: string;
}

const GROUPS_STORAGE_KEY = 'mmec_nav_groups_closed';

function readClosedGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(GROUPS_STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Record<string, boolean>;
  } catch {
    /* ignore */
  }
  return {};
}

/** Meta on Mac, Ctrl elsewhere — the label the palette shortcut is shown with. */
const MOD_KEY = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';

const css = `
  .mmec-nav-rail {
    height: 100%;
    background: var(--rail-bg);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: var(--font-ui);
    user-select: none;
  }

  /* ── Search affordance ─────────────────────────────────────────────── */
  .mmec-nav-search {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 10px 10px 6px;
    height: 32px;
    padding: 0 8px 0 10px;
    border: 1px solid var(--hair);
    border-radius: 8px;
    background: var(--base);
    color: var(--muted);
    font: 500 11.5px/1 var(--font-ui);
    cursor: pointer;
    transition:
      border-color var(--dur-micro) var(--ease),
      color var(--dur-micro) var(--ease),
      margin var(--dur-panel) var(--ease),
      padding var(--dur-panel) var(--ease);
    white-space: nowrap;
    overflow: hidden;
  }
  .mmec-nav-search:hover {
    border-color: var(--border);
    color: var(--txt);
  }
  .mmec-nav-search svg { flex-shrink: 0; }
  .mmec-nav-search-label {
    flex: 1;
    text-align: start;
    opacity: 1;
    transition: opacity var(--dur-control) var(--ease);
  }
  .mmec-nav-search .mmec-kbd {
    transition: opacity var(--dur-control) var(--ease);
  }

  /* ── Scroll region ─────────────────────────────────────────────────── */
  .mmec-nav-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    padding: 4px 0 12px;
  }

  /* The one element that moves: a 2px bar that glides to the active row.
     Positioned by JS measurement, animated by CSS. */
  .mmec-nav-indicator {
    position: absolute;
    inset-inline-start: 0;
    top: 0;
    width: 2px;
    height: 0;
    border-radius: 0 2px 2px 0;
    background: var(--rail-act);
    transform: translateY(var(--y, 0px));
    transition:
      transform var(--dur-panel) var(--ease),
      height var(--dur-panel) var(--ease),
      opacity var(--dur-control) var(--ease);
    opacity: 0;
    pointer-events: none;
  }
  .mmec-nav-indicator.is-visible { opacity: 1; }

  /* ── Group ─────────────────────────────────────────────────────────── */
  .mmec-nav-group { margin-top: 6px; }
  .mmec-nav-group:first-child { margin-top: 2px; }

  .mmec-nav-group-header {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    height: 26px;
    padding: 0 10px 0 14px;
    border: none;
    background: transparent;
    color: var(--muted-2);
    cursor: pointer;
    text-align: start;
    transition: color var(--dur-micro) var(--ease);
  }
  .mmec-nav-group-header:hover { color: var(--muted); }
  .mmec-nav-group-header:focus-visible { outline-offset: -2px; }

  .mmec-nav-group-label {
    flex: 1;
    min-width: 0;
    font: 600 10px/1 var(--font-ui);
    letter-spacing: 0.10em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: opacity var(--dur-control) var(--ease);
  }
  .mmec-nav-group-chevron {
    flex-shrink: 0;
    opacity: 0.6;
    transition: transform var(--dur-control) var(--ease), opacity var(--dur-control) var(--ease);
  }
  .mmec-nav-group-chevron.is-open { transform: rotate(90deg); }

  /* Collapsing groups: grid-row 1fr → 0fr animates height without measuring. */
  .mmec-nav-items {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows var(--dur-panel) var(--ease);
  }
  .mmec-nav-items.is-closed { grid-template-rows: 0fr; }
  .mmec-nav-items > div {
    min-height: 0;
    overflow: hidden;
  }

  /* ── Item ──────────────────────────────────────────────────────────── */
  .mmec-nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 32px;
    padding: 0 10px 0 14px;
    border: none;
    background: transparent;
    color: var(--txt-2);
    font: 400 12px/1 var(--font-ui);
    text-align: start;
    cursor: pointer;
    position: relative;
    transition:
      background var(--dur-micro) var(--ease),
      color var(--dur-micro) var(--ease);
  }
  .mmec-nav-item:hover {
    background: var(--rail-hover);
    color: var(--txt);
  }
  .mmec-nav-item.is-active {
    color: var(--txt);
    background: var(--rail-act-dim);
  }
  .mmec-nav-item:focus-visible { outline-offset: -2px; }
  .mmec-nav-item:active .mmec-nav-item-icon { transform: scale(0.9); }

  /* Collapsed rail: the label rides out beside the icon on hover, instantly —
     a native title tooltip takes a second to appear and reads as a stall.
     Rendered as one fixed element so no scroll container can clip it. */
  .mmec-nav-tip {
    position: fixed;
    z-index: 60;
    padding: 5px 9px;
    border-radius: 6px;
    background: var(--elev2);
    border: 1px solid var(--border);
    box-shadow: var(--lift-2);
    color: var(--txt);
    font: 500 11.5px/1 var(--font-ui);
    white-space: nowrap;
    pointer-events: none;
    transform: translateY(-50%);
    animation: mmec-tip-in var(--dur-control) var(--ease) both;
  }
  @keyframes mmec-tip-in {
    from { opacity: 0; transform: translate(-4px, -50%); }
    to   { opacity: 1; transform: translate(0, -50%); }
  }

  .mmec-nav-item-icon {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    display: grid;
    place-items: center;
    color: var(--muted);
    transition: color var(--dur-micro) var(--ease), transform var(--dur-micro) var(--ease);
  }
  .mmec-nav-item:hover .mmec-nav-item-icon { color: var(--txt-2); }
  .mmec-nav-item.is-active .mmec-nav-item-icon { color: var(--rail-act); }

  .mmec-nav-item-label {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: opacity var(--dur-control) var(--ease);
  }
  .mmec-nav-item.is-active .mmec-nav-item-label { font-weight: 500; }

  /* Tools kept for comparison read as such without a parenthetical. */
  .mmec-nav-item-tag {
    flex-shrink: 0;
    font: 500 9px/1 var(--font-mono);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--muted-2);
    border: 1px solid var(--hair);
    border-radius: 4px;
    padding: 2px 4px;
    transition: opacity var(--dur-control) var(--ease);
  }

  /* ── Footer ────────────────────────────────────────────────────────── */
  .mmec-nav-footer {
    flex-shrink: 0;
    border-top: 1px solid var(--hair);
    padding: 6px;
  }
  .mmec-nav-collapse {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 32px;
    padding: 0 8px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--muted);
    font: 500 11.5px/1 var(--font-ui);
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    transition: background var(--dur-micro) var(--ease), color var(--dur-micro) var(--ease);
  }
  .mmec-nav-collapse:hover { background: var(--rail-hover); color: var(--txt); }
  .mmec-nav-collapse svg { flex-shrink: 0; }
  .mmec-nav-collapse span { transition: opacity var(--dur-control) var(--ease); }

  /* ── Collapsed (icon) mode ─────────────────────────────────────────── */
  .mmec-nav-rail.is-collapsed .mmec-nav-search {
    margin: 10px 8px 6px;
    padding: 0;
    justify-content: center;
  }
  .mmec-nav-rail.is-collapsed .mmec-nav-search-label,
  .mmec-nav-rail.is-collapsed .mmec-nav-search .mmec-kbd,
  .mmec-nav-rail.is-collapsed .mmec-nav-group-label,
  .mmec-nav-rail.is-collapsed .mmec-nav-group-chevron,
  .mmec-nav-rail.is-collapsed .mmec-nav-item-label,
  .mmec-nav-rail.is-collapsed .mmec-nav-item-tag,
  .mmec-nav-rail.is-collapsed .mmec-nav-collapse span {
    opacity: 0;
    width: 0;
    overflow: hidden;
  }
  .mmec-nav-rail.is-collapsed .mmec-nav-group-header {
    height: 10px;
    padding: 0 14px;
    pointer-events: none;
  }
  .mmec-nav-rail.is-collapsed .mmec-nav-group-header::after {
    content: "";
    flex: 1;
    height: 1px;
    background: var(--hair);
  }
  .mmec-nav-rail.is-collapsed .mmec-nav-group-header .mmec-nav-group-glyph { display: none; }
  .mmec-nav-rail.is-collapsed .mmec-nav-group:first-child .mmec-nav-group-header { display: none; }
  .mmec-nav-rail.is-collapsed .mmec-nav-items.is-closed { grid-template-rows: 1fr; }
  .mmec-nav-rail.is-collapsed .mmec-nav-item {
    justify-content: center;
    padding: 0;
    gap: 0;
  }

  .mmec-nav-rail.is-collapsed .mmec-nav-collapse {
    justify-content: center;
    padding: 0;
    gap: 0;
  }

  .mmec-nav-group-glyph {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    opacity: 0.7;
  }
`;

export const MMECNavRail: React.FC<MMECNavRailProps> = ({
  categories,
  activeId,
  onSelect,
  collapsed = false,
  onToggleCollapsed,
  onOpenSearch,
  language = 'en',
}) => {
  const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>(readClosedGroups);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ y: number; h: number } | null>(null);
  const [tip, setTip] = useState<{ label: string; top: number; left: number } | null>(null);

  const showTip = useCallback((e: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>, label: string) => {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ label, top: r.top + r.height / 2, left: r.right + 8 });
  }, [collapsed]);
  const hideTip = useCallback(() => setTip(null), []);
  useEffect(() => {
    if (!collapsed) setTip(null);
  }, [collapsed]);

  const isAr = language === 'ar';

  useEffect(() => {
    try {
      localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(closedGroups));
    } catch {
      /* ignore */
    }
  }, [closedGroups]);

  const toggleGroup = useCallback((groupId: string) => {
    setClosedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  }, []);

  // Selecting a tool inside a closed group (from the palette, a bookmark, a
  // tour) opens that group so the active row is never hidden.
  const activeGroupId = useMemo(
    () => categories.find((c) => c.items.some((i) => i.id === activeId))?.id,
    [categories, activeId],
  );
  useEffect(() => {
    if (activeGroupId && closedGroups[activeGroupId]) {
      setClosedGroups((prev) => ({ ...prev, [activeGroupId]: false }));
    }
    // Only when the active tool changes — not when the user closes the group by hand.
  }, [activeGroupId]);

  // Position the indicator on the active row. Re-measured whenever anything
  // that could move rows changes; the group-collapse transition is the one
  // case where the target moves over time, so it is re-read when it ends.
  const measure = useCallback(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const el = scroll.querySelector<HTMLElement>(`[data-nav-id="${CSS.escape(activeId)}"]`);
    if (!el) {
      setIndicator(null);
      return;
    }
    const y = el.offsetTop;
    setIndicator({ y, h: el.offsetHeight });
  }, [activeId]);

  useLayoutEffect(() => {
    measure();
  }, [measure, categories, closedGroups, collapsed]);

  useEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const onEnd = () => measure();
    scroll.addEventListener('transitionend', onEnd);
    return () => scroll.removeEventListener('transitionend', onEnd);
  }, [measure]);

  // Keep the active row in view when the tool changes by another route.
  useEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const el = scroll.querySelector<HTMLElement>(`[data-nav-id="${CSS.escape(activeId)}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const dsAr = isAr && isDesignSystemOn();

  const collapseLabel = collapsed
    ? isAr ? 'توسيع القائمة' : 'Expand navigation'
    : isAr ? 'طي القائمة' : 'Collapse navigation';

  return (
    <>
      <style>{css}</style>
      <nav
        className={`mmec-nav-rail${collapsed ? ' is-collapsed' : ''}`}
        aria-label={isAr ? 'الأدوات' : 'Tools'}
      >
        {isDesignSystemOn() && (
          <a href="/" className="mmec-ds-rail-brand" aria-label="MMEC home">
            {/* The MMEC mark, as in the top bar before the redesign (owner: the logo stays; the hair is an indicator). */}
            <span className="mmec-brand-mark" aria-hidden>M</span>
            <b>MMEC</b>
          </a>
        )}
        {onOpenSearch && (
          <button
            type="button"
            className="mmec-nav-search"
            onClick={onOpenSearch}
            title={`${isAr ? 'ابحث عن أداة' : 'Search tools'} (${MOD_KEY} K)`}
            aria-label={isAr ? 'ابحث عن أداة' : 'Search tools'}
          >
            <LuSearch size={14} strokeWidth={2} aria-hidden />
            <span className="mmec-nav-search-label">{isAr ? 'ابحث عن أداة' : 'Search tools'}</span>
            <span className="mmec-kbd" aria-hidden>{MOD_KEY} K</span>
          </button>
        )}

        <div className="mmec-nav-scroll" ref={scrollRef}>
          <div
            className={`mmec-nav-indicator${indicator ? ' is-visible' : ''}`}
            style={
              indicator
                ? ({ '--y': `${indicator.y}px`, height: indicator.h } as React.CSSProperties)
                : undefined
            }
            aria-hidden
          />

          {categories.map((category) => {
            const isClosed = closedGroups[category.id] ?? false;
            const groupBodyId = `mmec-nav-group-${category.id}`;
            return (
              <div key={category.id} className="mmec-nav-group">
                <button
                  type="button"
                  className="mmec-nav-group-header"
                  onClick={() => toggleGroup(category.id)}
                  aria-expanded={!isClosed}
                  aria-controls={groupBodyId}
                  tabIndex={collapsed ? -1 : 0}
                >
                  <span className="mmec-nav-group-glyph">
                    <NavIcon id={category.id} kind="group" fallback={category.icon} size={12} />
                  </span>
                  <span className="mmec-nav-group-label">{dsAr ? navLabel(category.title, true) : category.title}</span>
                  <LuChevronRight
                    size={11}
                    className={`mmec-nav-group-chevron${isClosed ? '' : ' is-open'}`}
                    aria-hidden
                  />
                </button>

                <div id={groupBodyId} className={`mmec-nav-items${isClosed ? ' is-closed' : ''}`}>
                  <div>
                    {category.items.map((item) => {
                      const isActive = activeId === item.id;
                      // "Options Flow (old)" → label "Options Flow", tag "old".
                      const m = item.title.match(/^(.*?)\s*\((old|legacy|beta)\)\s*$/i);
                      const label = dsAr ? navLabel(m ? m[1] : item.title, true) : m ? m[1] : item.title;
                      const tag = m ? m[2].toLowerCase() : null;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          data-nav-id={item.id}
                          data-label={item.title}
                          className={`mmec-nav-item${isActive ? ' is-active' : ''}`}
                          onClick={() => onSelect(item.id)}
                          onMouseEnter={(e) => showTip(e, item.title)}
                          onMouseLeave={hideTip}
                          onFocus={(e) => showTip(e, item.title)}
                          onBlur={hideTip}
                          title={collapsed ? undefined : item.title}
                          aria-current={isActive ? 'page' : undefined}
                          tabIndex={!collapsed && isClosed ? -1 : 0}
                        >
                          <span className="mmec-nav-item-icon">
                            <NavIcon id={item.id} fallback={item.icon} size={16} />
                          </span>
                          <span className="mmec-nav-item-label">{label}</span>
                          {tag && <span className="mmec-nav-item-tag">{tag}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {tip && (
          <div className="mmec-nav-tip" role="tooltip" style={{ top: tip.top, left: tip.left }}>
            {tip.label}
          </div>
        )}

        {onToggleCollapsed && (
          <div className="mmec-nav-footer">
            <button
              type="button"
              className="mmec-nav-collapse"
              onClick={onToggleCollapsed}
              title={collapseLabel}
              aria-label={collapseLabel}
              aria-expanded={!collapsed}
            >
              {collapsed ? (
                <LuPanelLeftOpen size={16} strokeWidth={1.75} aria-hidden />
              ) : (
                <LuPanelLeftClose size={16} strokeWidth={1.75} aria-hidden />
              )}
              <span>{isAr ? 'طي' : 'Collapse'}</span>
            </button>
          </div>
        )}
      </nav>
    </>
  );
};

export default MMECNavRail;
